import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useLanguage } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Loader2, RefreshCw, Search, Trophy, PauseCircle, User as UserIcon,
  Phone, Instagram, MessageSquare, Crown, Shield, Filter, Users as UsersIcon,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_META = {
  new:            { it: "Nuovi",          en: "New",          color: "from-slate-500 to-slate-600" },
  contacted:      { it: "Contattati",     en: "Contacted",    color: "from-sky-500 to-blue-600" },
  interested:     { it: "Interessati",    en: "Interested",   color: "from-violet-500 to-purple-600" },
  demo_booked:    { it: "Demo fissata",   en: "Demo booked",  color: "from-amber-500 to-orange-600" },
  trial:          { it: "Trial",          en: "Trial",        color: "from-rose-500 to-pink-600" },
  closed_won:     { it: "Chiuso ✓",       en: "Won",          color: "from-emerald-500 to-teal-600" },
  recall_later:   { it: "Da risentire più avanti", en: "Recall later", color: "from-orange-500 to-amber-600" },
  no_reply:       { it: "Mai Risposto",   en: "No reply",     color: "from-rose-500 to-red-600" },
  not_interested: { it: "Non interessato", en: "Not interested", color: "from-zinc-400 to-zinc-500" },
};

const fmtRel = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diff <= 0) return "oggi";
  if (diff === 1) return "ieri";
  if (diff < 30) return `${diff}g fa`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
};

const AdminPipelineDashboard = () => {
  const { language } = useLanguage();
  const t = (it, en) => language === "it" ? it : en;
  const [data, setData] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSales, setFilterSales] = useState("all");
  const [filterSector, setFilterSector] = useState("all");
  const [search, setSearch] = useState("");
  const [searchDeb, setSearchDeb] = useState("");
  const [draggingLead, setDraggingLead] = useState(null); // { lead_id, from_stage }
  const [dragOverStage, setDragOverStage] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDeb(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterSales !== "all") params.sales_id = filterSales;
      if (filterSector !== "all") params.sector = filterSector;
      if (searchDeb) params.search = searchDeb;
      const [pipe, ass] = await Promise.all([
        axios.get(`${API}/sales/admin/pipeline`, { params, withCredentials: true }),
        assignees.length === 0
          ? axios.get(`${API}/sales/admin/assignees`, { withCredentials: true })
          : Promise.resolve({ data: { assignees } }),
      ]);
      setData(pipe.data);
      if (ass?.data?.assignees) setAssignees(ass.data.assignees);
    } catch (err) {
      toast.error(t("Errore nel caricamento", "Loading error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterSales, filterSector, searchDeb]);

  // -----------------------------------------------------------------
  // Drag & drop: move a lead card from one stage column to another.
  // Strategy: optimistic UI update + PATCH /leads/{id}/status, revert on failure.
  // -----------------------------------------------------------------
  const handleDragStart = (lead, fromStage) => {
    setDraggingLead({ lead_id: lead.lead_id, from_stage: fromStage });
  };
  const handleDragEnd = () => {
    setDraggingLead(null);
    setDragOverStage(null);
  };
  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStage !== stage) setDragOverStage(stage);
  };
  const handleDragLeave = (stage) => {
    if (dragOverStage === stage) setDragOverStage(null);
  };
  const handleDrop = async (e, toStage) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!draggingLead || draggingLead.from_stage === toStage) {
      setDraggingLead(null);
      return;
    }
    const { lead_id, from_stage } = draggingLead;
    setDraggingLead(null);

    // Optimistic UI update
    const snapshot = data;
    setData(prev => {
      if (!prev) return prev;
      const newByStage = { ...prev.by_stage };
      const fromList = (newByStage[from_stage] || []).slice();
      const toList = (newByStage[toStage] || []).slice();
      const idx = fromList.findIndex(l => l.lead_id === lead_id);
      if (idx === -1) return prev;
      const [moved] = fromList.splice(idx, 1);
      const movedUpdated = { ...moved, status: toStage, updated_at: new Date().toISOString() };
      toList.unshift(movedUpdated);
      newByStage[from_stage] = fromList;
      newByStage[toStage] = toList;
      const newStageCounts = { ...(prev.stage_counts || {}) };
      newStageCounts[from_stage] = Math.max(0, (newStageCounts[from_stage] || 0) - 1);
      newStageCounts[toStage] = (newStageCounts[toStage] || 0) + 1;
      return { ...prev, by_stage: newByStage, stage_counts: newStageCounts };
    });

    try {
      await axios.patch(
        `${API}/leads/${lead_id}/status`,
        { status: toStage },
        { withCredentials: true }
      );
      const newLabel = STAGE_META[toStage]?.[language] || toStage;
      toast.success(t(`Lead spostata in "${newLabel}"`, `Lead moved to "${newLabel}"`));
    } catch (err) {
      // Rollback on failure
      setData(snapshot);
      toast.error(t("Impossibile aggiornare lo stato", "Failed to update status"));
    }
  };

  const sectors = useMemo(() => {
    if (!data) return [];
    const set = new Set();
    Object.values(data.by_stage || {}).forEach(arr => arr.forEach(l => l.sector && set.add(l.sector)));
    return Array.from(set).sort();
  }, [data]);

  return (
    <div className="space-y-6" data-testid="admin-pipeline-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <UsersIcon className="w-6 h-6 text-violet-500" />
            {t("Pipeline · Vista globale", "Pipeline · Global view")}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t(
              "Vista cross-sales di tutte le lead e dei loro stadi nella pipeline.",
              "Cross-sales view of all leads and their pipeline stages."
            )}
          </p>
        </div>
        <Button
          variant="outline" size="sm" onClick={load}
          className="rounded-full" data-testid="pipeline-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          {t("Aggiorna", "Refresh")}
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="pipeline-filters">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Cerca per nome, telefono, email, IG…", "Search…")}
            className="pl-9 rounded-full"
            data-testid="pipeline-search"
          />
        </div>
        <Select value={filterSales} onValueChange={setFilterSales}>
          <SelectTrigger className="rounded-full" data-testid="pipeline-filter-sales">
            <SelectValue placeholder={t("Tutti gli owner", "All owners")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("Tutti gli owner", "All owners")}</SelectItem>
            <SelectItem value="_unassigned">{t("Non assegnati", "Unassigned")}</SelectItem>
            {assignees.map(u => (
              <SelectItem key={u.user_id} value={u.user_id}>
                {u.role === "super_admin" ? "👑 " : ""}{u.name || u.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterSector} onValueChange={setFilterSector}>
          <SelectTrigger className="rounded-full" data-testid="pipeline-filter-sector">
            <SelectValue placeholder={t("Tutti i settori", "All sectors")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("Tutti i settori", "All sectors")}</SelectItem>
            {sectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : !data ? null : (
        <>
          {/* Stage counters */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="pipeline-stage-counters">
            {data.stages.map(s => {
              const meta = STAGE_META[s] || { it: s, en: s, color: "from-slate-500 to-slate-600" };
              const count = data.stage_counts?.[s] || 0;
              return (
                <div key={s} className={`rounded-xl bg-gradient-to-br ${meta.color} text-white p-3 shadow-sm`}>
                  <p className="text-2xl font-black">{count}</p>
                  <p className="text-[10px] uppercase tracking-wider font-bold opacity-90">{t(meta.it, meta.en)}</p>
                </div>
              );
            })}
          </div>

          {/* Owner summary */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4" data-testid="pipeline-owner-summary">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-500" />
              <h3 className="font-semibold text-sm">
                {t("Riepilogo per owner", "Summary by owner")}
                <span className="text-xs text-muted-foreground font-normal ml-2">
                  · {data.total} {t("lead totali", "total leads")}
                </span>
              </h3>
            </div>
            {data.by_owner.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t("Nessuna lead nei filtri selezionati.", "No leads in the selected filters.")}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {data.by_owner.map(o => (
                  <div
                    key={o.owner_id}
                    className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-violet-400 hover:bg-violet-50/50 dark:hover:bg-violet-950/20 cursor-pointer transition-colors"
                    onClick={() => setFilterSales(o.owner_id)}
                    data-testid={`pipeline-owner-${o.owner_id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 shadow ${
                      o.owner_role === "super_admin" ? "bg-gradient-to-br from-amber-500 to-orange-600"
                      : o.owner_role === "unassigned" ? "bg-slate-300 dark:bg-slate-700"
                      : "bg-gradient-to-br from-violet-500 to-purple-600"
                    }`}>
                      {o.owner_role === "super_admin" ? <Crown className="w-4 h-4" />
                      : o.owner_role === "unassigned" ? <UserIcon className="w-4 h-4" />
                      : (o.owner_name || "S").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate flex items-center gap-1">
                        {o.owner_name}
                        {o.owner_role === "super_admin" && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300">ADMIN</span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {o.total} {t("lead", "leads")}
                        {" · "}<span className="text-emerald-600 dark:text-emerald-400 font-semibold">{o.won} {t("won", "won")}</span>
                        {" · "}{o.active} {t("attivi", "active")}
                        {o.parked > 0 && <> · {o.parked} {t("pausa", "parked")}</>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Kanban */}
          <p className="text-[11px] text-muted-foreground -mb-1 italic" data-testid="pipeline-dnd-hint">
            {t("✋ Trascina una lead da una colonna all'altra per cambiarne lo stato. L'aggiornamento si propaga in tutte le tab.",
               "✋ Drag a lead from one column to another to change its status. The update propagates across all tabs.")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="pipeline-kanban">
            {data.stages.map(s => {
              const meta = STAGE_META[s] || { it: s, en: s, color: "from-slate-500 to-slate-600" };
              const list = data.by_stage?.[s] || [];
              const isDropTarget = dragOverStage === s && draggingLead && draggingLead.from_stage !== s;
              return (
                <div
                  key={s}
                  onDragOver={(e) => handleDragOver(e, s)}
                  onDragLeave={() => handleDragLeave(s)}
                  onDrop={(e) => handleDrop(e, s)}
                  className={`flex flex-col rounded-2xl border overflow-hidden transition-all ${
                    isDropTarget
                      ? "border-violet-500 ring-2 ring-violet-400/50 bg-violet-50 dark:bg-violet-950/30 scale-[1.01]"
                      : "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40"
                  }`}
                >
                  <div className={`px-3 py-2 bg-gradient-to-r ${meta.color} text-white flex items-center justify-between`}>
                    <span className="text-xs font-bold uppercase tracking-wider">{t(meta.it, meta.en)}</span>
                    <span className="text-[10px] font-bold bg-white/25 px-1.5 py-0.5 rounded-full">{list.length}</span>
                  </div>
                  <div className="flex-1 p-2 space-y-2 max-h-[600px] overflow-y-auto" data-testid={`pipeline-col-${s}`}>
                    {list.length === 0 ? (
                      <p className="text-[11px] text-center text-muted-foreground py-6">
                        {isDropTarget ? t("Rilascia qui per spostare ↓", "Drop here to move ↓") : t("Nessuna lead", "No leads")}
                      </p>
                    ) : (
                      list.map(l => (
                        <PipelineCard
                          key={l.lead_id}
                          lead={l}
                          t={t}
                          stage={s}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          isDragging={draggingLead?.lead_id === l.lead_id}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const PipelineCard = ({ lead, t, stage, onDragStart, onDragEnd, isDragging }) => {
  const isAdmin = lead.owner_role === "super_admin";
  const isWon = lead.status === "closed_won";
  const isParked = lead.status === "not_interested";
  return (
    <div
      draggable
      onDragStart={(e) => {
        try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", lead.lead_id); } catch { /* noop */ }
        onDragStart?.(lead, stage);
      }}
      onDragEnd={onDragEnd}
      title={t("Trascina per cambiare stato", "Drag to change status")}
      className={`p-2.5 rounded-xl bg-white dark:bg-slate-900 border text-xs transition-all cursor-grab active:cursor-grabbing select-none ${
        isDragging ? "opacity-40 scale-95 shadow-2xl"
        : isWon ? "border-emerald-400/50 bg-emerald-50/50 dark:bg-emerald-950/20"
        : isParked ? "border-slate-200 dark:border-slate-700 opacity-75"
        : "border-slate-200 dark:border-slate-800 hover:border-violet-400 hover:shadow-md"
      }`}
      data-testid={`pipeline-card-${lead.lead_id}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 dark:text-white truncate">{lead.name || "—"}</p>
          {lead.sector && <p className="text-[10px] text-muted-foreground truncate">{lead.sector}</p>}
        </div>
        {isWon && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 flex items-center gap-0.5">
            <Trophy className="w-2.5 h-2.5" /> WON
          </span>
        )}
        {isParked && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 flex items-center gap-0.5">
            <PauseCircle className="w-2.5 h-2.5" /> PAUSA
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-1.5 text-[10px] text-muted-foreground">
        {lead.phone && <span className="inline-flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{lead.phone}</span>}
        {lead.instagram_username && <span className="inline-flex items-center gap-0.5"><Instagram className="w-2.5 h-2.5" />{lead.instagram_username}</span>}
        {lead.city && <span>📍 {lead.city}</span>}
      </div>

      {isWon && lead.first_year_revenue && (
        <p className="mt-1.5 text-[10px] font-bold text-emerald-700 dark:text-emerald-300">
          €{Number(lead.first_year_revenue).toLocaleString("it-IT")} · {lead.plan_sold || ""}
        </p>
      )}

      <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${isAdmin ? "text-amber-700 dark:text-amber-300" : "text-violet-700 dark:text-violet-300"}`}>
          {isAdmin ? <Crown className="w-2.5 h-2.5" /> : lead.owner_name ? <UserIcon className="w-2.5 h-2.5" /> : <Shield className="w-2.5 h-2.5" />}
          <span className="truncate max-w-[110px]">{lead.owner_name || t("Non assegnato", "Unassigned")}</span>
        </span>
        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
          {fmtRel(lead.updated_at || lead.created_at)}
        </span>
      </div>
    </div>
  );
};

export default AdminPipelineDashboard;
